import { useState, useEffect, useCallback } from 'react';
import {
  getApiBaseUrl,
  createApiHeaders,
  apiFetch,
} from '../../../../config/api';
import { REQUEST_TIMEOUT_LLM_EXECUTION } from '../../../../config/constants';

/**
 * Hook for managing execution data and operations.
 *
 * @param {string} templateId - Template ID
 * @param {string} tenant - Tenant identifier
 * @param {string} templateName - Optional template name for logging
 * @returns {Object} Execution data, loading states, and execution functions
 */
export const useExecutionData = (templateId, tenant, templateName = null) => {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const loadExecutions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const headers = createApiHeaders();

      const response = await fetch(
        `${getApiBaseUrl()}/api/v1/templates/executions/?template_id=${templateId}`,
        {
          method: 'GET',
          headers,
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to load executions: ${response.status}`);
      }

      const data = await response.json();
      setExecutions(data.executions || []);
    } catch (err) {
      setError(err.message);
      setExecutions([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, tenant]);

  const executeTemplate = useCallback(
    async payload => {
      try {
        setExecuting(true);
        setError(null);
        setSuccess(null);

        const headers = createApiHeaders();

        // Use advanced endpoint if version_number is provided, otherwise use basic endpoint
        const hasVersion = payload.version_number != null;
        const endpoint = hasVersion
          ? `/api/v1/templates/advanced/execute`
          : `/api/v1/templates/executions/${templateId}/execute`;

        // For all frontend executions, always allow draft versions
        const requestPayload = hasVersion
          ? {
              ...payload,
              template_id: templateId,
              template_name: templateName || undefined,
              allow_draft: true,
              async_mode: true,
            }
          : { ...payload, allow_draft: true };

        // Create async job - use apiFetch with 300 second timeout for template execution
        const jobResponse = await apiFetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify(requestPayload),
          timeout: REQUEST_TIMEOUT_LLM_EXECUTION, // 300 seconds for template execution
        });

        // Check if async mode was used (has job_id)
        if (jobResponse.job_id) {
          // Async mode: Poll for job status with fixed 3 second interval (no exponential backoff)
          const jobId = jobResponse.job_id;
          const pollDelay = 3000; // Fixed 3 seconds interval
          const maxPollTime = 330000; // 5.5 minutes (330 seconds) - matches backend job timeout of 300s + 30s buffer
          const startTime = Date.now();

          while (Date.now() - startTime < maxPollTime) {
            await new Promise(resolve => setTimeout(resolve, pollDelay));

            const statusResponse = await fetch(
              `${getApiBaseUrl()}/api/v1/templates/advanced/jobs/${jobId}/status`,
              { headers }
            );

            if (!statusResponse.ok) {
              throw new Error(
                `Failed to get job status: ${statusResponse.status}`
              );
            }

            const statusData = await statusResponse.json();

            if (statusData.status === 'completed') {
              const result = statusData.result;
              // Debug logging
              console.log(
                '🔍 [FRONTEND DEBUG] Job completed, received result:',
                {
                  has_result: result != null,
                  result_keys: result ? Object.keys(result) : [],
                  has_structured_output: result?.structured_output != null,
                  structured_output_type: typeof result?.structured_output,
                  structured_output_value: result?.structured_output,
                  has_output_data: result?.output_data != null,
                  output_format: result?.output_format,
                }
              );
              setSuccess('Template executed successfully!');
              await loadExecutions();
              setExecuting(false);
              return result;
            } else if (
              statusData.status === 'failed' ||
              statusData.status === 'timeout'
            ) {
              throw new Error(statusData.error || `Job ${statusData.status}`);
            }

            // Fixed interval - continue polling with 3 second delay
          }

          throw new Error('Job polling timeout - request took too long');
        } else {
          // Synchronous mode (fallback for basic endpoint)
          setSuccess('Template executed successfully!');
          await loadExecutions();
          setExecuting(false);
          return jobResponse;
        }
      } catch (err) {
        setError(err.message);
        setExecuting(false);
        throw err;
      }
    },
    [templateId, templateName, loadExecutions]
  );

  const executeTemplateSimplified = useCallback(
    async payload => {
      try {
        setExecuting(true);
        setError(null);
        setSuccess(null);

        const headers = createApiHeaders();

        // For all frontend executions, always allow draft versions
        const requestPayload = { ...payload, allow_draft: true };

        // Use apiFetch with 300 second timeout for template execution
        const result = await apiFetch(
          `/api/v1/templates/executions/${templateId}/execute-simplified`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify(requestPayload),
            timeout: REQUEST_TIMEOUT_LLM_EXECUTION, // 300 seconds for template execution
          }
        );
        setSuccess('Template executed successfully with experiment support!');
        await loadExecutions();
        return result;
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setExecuting(false);
      }
    },
    [templateId, loadExecutions]
  );

  useEffect(() => {
    if (templateId) {
      loadExecutions();
    }
  }, [templateId, loadExecutions]);

  return {
    executions,
    loading,
    executing,
    error,
    success,
    loadExecutions,
    executeTemplate,
    executeTemplateSimplified,
    setError,
    setSuccess,
  };
};
